import { fetchUtils } from 'react-admin'
import { stringify } from 'query-string'

const apiUrl = process.env.REACT_APP_BACKEND_URL
const studioUrl = process.env.REACT_APP_STUDIO_URL

const filterRecordingUpdateData = (data = {}) => {
    const {
        transcripts,
        imageCaptions,
        soundCaptions,
        _transcripts,
        _imageCaptions,
        _soundCaptions,
        ...filtered
    } = data
    return filtered
}

const getTranscriptEntryId = (entry = {}) => entry._id || entry.id

const diffTranscriptNames = (current = [], previous = []) => {
    const currentEntries = Array.isArray(current) ? current : []
    const previousEntries = Array.isArray(previous) ? previous : []
    const previousById = new Map(
        previousEntries
            .map((entry) => [getTranscriptEntryId(entry), entry?.name || ''])
            .filter(([id]) => Boolean(id))
    )

    return currentEntries
        .map((entry) => {
            const id = getTranscriptEntryId(entry)
            if (!id) return null
            const name = entry?.name || ''
            if (previousById.get(id) === name) return null
            return { id, name }
        })
        .filter(Boolean)
}

const getRecordingTranscriptNameUpdates = (data = {}, previousData = {}) => {
    const updates = [
        ...diffTranscriptNames(data._transcripts, previousData._transcripts),
        ...diffTranscriptNames(
            data._imageCaptions,
            previousData._imageCaptions
        ),
        ...diffTranscriptNames(data._soundCaptions, previousData._soundCaptions),
    ]

    // remove duplicates
    return Array.from(new Map(updates.map((entry) => [entry.id, entry])).values())
}

const updateTranscriptNames = async (updates = []) => {
    if (!studioUrl || !Array.isArray(updates) || updates.length === 0) return

    await Promise.all(
        updates.map(({ id, name }) =>
            httpClient(`${studioUrl}/transcript?${stringify({ id })}`, {
                method: 'PUT',
                headers: new Headers({
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                }),
                body: JSON.stringify({ name }),
            })
        )
    )
}

const populateTranscriptPath = (entry = {}) => ({
    ...entry,
    path: entry?.path || entry?.tempResourceUrl || entry?.azureResourceUrl || '',
})

const populateTranscriptPaths = (record = {}) => {
    const mapCollection = (collection) =>
        Array.isArray(collection)
            ? collection.map((entry) => populateTranscriptPath(entry))
            : collection

    return {
        ...record,
        _transcripts: mapCollection(record._transcripts),
        _imageCaptions: mapCollection(record._imageCaptions),
        _soundCaptions: mapCollection(record._soundCaptions),
    }
}

const httpClient = (url, options = {}) => {
    if (!options.headers)
        options.headers = new Headers({
            Accept: 'application/json',
        })
    const token = localStorage.getItem('token')
    options.headers.set('Authorization', `Bearer ${token}`)
    return fetchUtils.fetchJson(url, options)
}

const fileUploadClient = (url, formData, progressCallback) => {
    const token = localStorage.getItem('token')
    const xhr = new XMLHttpRequest()
    xhr.upload.onprogress = progressCallback
    xhr.open('POST', url)
    xhr.setRequestHeader('Accept', 'application/json')
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.send(formData)
    return xhr
}

const deleteRecordingById = (id) =>
    httpClient(`${studioUrl}/media?${stringify({ id })}`, {
        method: 'DELETE',
    }).then(({ json }) => ({ data: json }))

export default {
    getCustom: (resource, query) => {
        const url = `${apiUrl}/${resource}?${stringify(query)}`

        return httpClient(url).then(({ headers, json }) => {
            return json
        })
    },
    getList: (resource, params) => {
        const { page, perPage: limit } = params.pagination
        const { field: sort, order } = params.sort
        const query = {
            sort: sort,
            order: order,
            page: page,
            limit: limit,
            //filter not supported yet
            ...(params.filter && params.filter.key && params.filter.fields
                ? {
                    filter: params.filter.key,
                    fields: params.filter.fields.join(','),
                }
                : {}),
        }
        const url = `${apiUrl}/${resource}?${stringify(query)}`
        return httpClient(url).then(({ headers, json }) => {
            const { docs, ...meta } = json
            return {
                data:
                    resource === 'recordings'
                        ? docs.map((doc) => populateTranscriptPaths(doc))
                        : docs,
                meta: meta,
                total: meta.totalDocs,
            }
        })
    },
    getListTextSearch: (resource, text, limit, page, sort, order) => {
        const query = {
            filter: JSON.stringify({
                $text: {
                    $search: text || '$',
                    $caseSensitive: false,
                },
            }),
            limit: limit || 5,
            page: page || 1,
            sort: sort,
            order: order,
        }
        const url = `${apiUrl}/${resource}?${stringify(query)}`

        return httpClient(url).then(({ headers, json }) => {
            const { docs, ...meta } = json
            return {
                data: docs,
                meta: meta,
                total: meta.totalDocs,
            }
        })
    },
    getResultsByCaptions: (
        resource,
        _text,
        limit,
        page,
        sort,
        order,
        fields
    ) => {
        const getFieldRegex = (field, text) => ({
            [field]: {
                $regex: `(${text.trim().split(' ').join('|')})`,
                $options: 'i',
            },
        })
        const query = {
            filter: JSON.stringify({
                $or: Object.keys(fields).map((f) =>
                    getFieldRegex(f, fields[f])
                ),
            }),
            limit: limit || 5,
            page: page || 1,
            sort: sort,
            order: order,
        }
        const url = `${apiUrl}/${resource}?${stringify(query)}`

        return httpClient(url).then(({ headers, json }) => {
            const { docs, ...meta } = json
            return {
                data: docs,
                meta: meta,
                total: meta.totalDocs,
            }
        })
    },

    getOne: (resource, params) =>
        httpClient(`${apiUrl}/${resource}/${params.id}`).then(({ json }) => ({
            data: {
                ...(resource === 'recordings'
                    ? populateTranscriptPaths(json)
                    : json),
                id: json._id,
            },
        })),

    getStatic: (resource) => httpClient(`${apiUrl}/static/${resource}`),

    getMany: (resource, params) => {
        const query = {
            filter: JSON.stringify({ _id: params.ids }),
        }
        const url = `${apiUrl}/${resource}?${stringify(query)}`
        return httpClient(url).then(({ headers, json }) => {
            const { docs, ...meta } = json
            return {
                data: docs,
                meta: meta,
                total: meta.totalDocs,
            }
        })
    },

    //getManyReference not supported yet
    getManyReference: (resource, params) => {
        const { page, perPage } = params.pagination
        const { field, order } = params.sort
        const query = {
            sort: JSON.stringify([field, order]),
            range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
            filter: JSON.stringify({
                ...params.filter,
                [params.target]: params.id,
            }),
        }
        const url = `${apiUrl}/${resource}?${stringify(query)}`

        return httpClient(url).then(({ headers, json }) => ({
            data: json,
            total: parseInt(headers.get('content-range').split('/').pop(), 10),
        }))
    },

    update: async (resource, params) => {
        const isRecording = resource === 'recordings'
        const updateData = isRecording
            ? filterRecordingUpdateData(params.data)
            : params.data
        const transcriptNameUpdates = isRecording
            ? getRecordingTranscriptNameUpdates(params.data, params.previousData)
            : []

        const { json } = await httpClient(`${apiUrl}/${resource}/${params.id}`, {
            method: 'PATCH',
            body: JSON.stringify(updateData),
        })

        if (isRecording && transcriptNameUpdates.length > 0) {
            await updateTranscriptNames(transcriptNameUpdates)
        }

        return {
            data: {
                ...(isRecording ? populateTranscriptPaths(json) : json),
                id: json._id,
            },
        }
    },

    // updateMany not supported yet
    updateMany: (resource, params) => {
        const query = {
            filter: JSON.stringify({ id: params.ids }),
        }
        return httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
            method: 'PATCH',
            body: JSON.stringify(params.data),
        }).then(({ json }) => ({ data: json }))
    },

    create: (resource, params) => {
        if (resource === 'users') {
            return httpClient(`${apiUrl}/register`, {
                method: 'POST',
                body: JSON.stringify(params.data),
            }).then(({ json }) => ({
                data: { ...json.user, id: json.user._id },
            }))
        }
        else {
            return httpClient(`${apiUrl}/${resource}`, {
                method: 'POST',
                body: JSON.stringify(params.data),
            }).then(({ json }) => ({
                data: json,
            }))
        }
    },

    delete: (resource, params) =>
        resource === 'recordings'
            ? deleteRecordingById(params.id)
            : httpClient(`${apiUrl}/${resource}/${params.id}`, {
                method: 'DELETE',
            }).then(({ json }) => ({ data: json })),

    deleteMany: (resource, params) => {
        if (resource === 'recordings') {
            return Promise.all(
                params.ids.map((id) => deleteRecordingById(id))
            ).then(() => ({ data: params.ids }))
        }

        return Promise.all(
            params.ids.map((id) =>
                httpClient(`${apiUrl}/${resource}/${id}`, {
                    method: 'DELETE',
                })
            )
        ).then(() => ({ data: params.ids }))
    },
    
    uploadFile: (path, field, file, mediaID, progressCallback = () => { }) => {
        const data = new FormData()
        data.append(field, file, file.name)
        //console.log(`${studioUrl}/${path}`)
        return fileUploadClient(`${studioUrl}/${path}`, data, progressCallback)
    },
}
